import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { HelpCircle, Phone, Mail, MapPin, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const FAQItem = ({ question, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors duration-200"
      >
        <span className="text-lg font-semibold text-[#00594C] pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#00594C] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#00594C] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 bg-white">
          <div className="text-gray-600 leading-relaxed">{children}</div>
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const contactMethods = [
    {
      icon: Phone,
      title: 'Phone',
      primary: '0468 007 056',
      href: 'tel:+61468007056',
      detail: 'Available Mon-Fri 9am-5pm AEST',
    },
    {
      icon: Mail,
      title: 'Email',
      primary: 'admin@manageyourloans.com.au',
      href: 'mailto:admin@manageyourloans.com.au',
      detail: "We'll respond within 24 hours",
    },
    {
      icon: MapPin,
      title: 'Office',
      primary: 'Level 1, 18-20 Knuckey St',
      href: 'https://maps.google.com/?q=Level+1,+18-20+Knuckey+St+Darwin+NT+0800',
      detail: 'Darwin NT 0800',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Support"
        description="Get help from Manage Your Loans. Contact us by phone, email, or visit our Darwin office. Find answers to common questions."
        path="/support"
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <HelpCircle className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">How Can We Help?</h1>
            <p className="text-lg text-gray-200">
              Get in touch with our team or find answers to common questions below
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {contactMethods.map((method) => (
                <a
                  key={method.title}
                  href={method.href}
                  target={method.title === 'Office' ? '_blank' : undefined}
                  rel={method.title === 'Office' ? 'noopener noreferrer' : undefined}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 text-center group"
                >
                  <div className="w-14 h-14 bg-[#99DF63]/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#99DF63]/30 transition-colors duration-300">
                    <method.icon className="w-7 h-7 text-[#00594C]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#00594C] mb-2">{method.title}</h3>
                  <p className="text-[#00594C] font-semibold mb-1">{method.primary}</p>
                  <p className="text-gray-500 text-sm">{method.detail}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Common Questions</h2>
              <p className="text-gray-600 text-lg">Quick answers to frequently asked questions</p>
            </div>
            <div className="space-y-4">
              <FAQItem question="How do I check the status of my loan application?">
                <p>
                  You can contact your broker directly for the most up-to-date information on your application. Alternatively, call us on{' '}
                  <a href="tel:+61468007056" className="text-[#00594C] font-semibold hover:underline">
                    0468 007 056
                  </a>{' '}
                  and our team will be happy to assist you.
                </p>
              </FAQItem>

              <FAQItem question="I need to update my personal details">
                <p>
                  Please email{' '}
                  <a href="mailto:admin@manageyourloans.com.au" className="text-[#00594C] font-semibold hover:underline">
                    admin@manageyourloans.com.au
                  </a>{' '}
                  with your updated information and we'll make the necessary changes to your file.
                </p>
              </FAQItem>

              <FAQItem question="How do I make a complaint?">
                <p>
                  We take all feedback seriously and have a formal complaints resolution process in place. Please visit our{' '}
                  <a href="/complaints-policy" className="text-[#00594C] font-semibold hover:underline">
                    Complaints Policy
                  </a>{' '}
                  page for full details on how to lodge a complaint and what to expect during the resolution process.
                </p>
              </FAQItem>

              <FAQItem question="What documents do I need for my loan application?">
                <p>While requirements can vary depending on your situation, you will generally need:</p>
                <ul className="mt-3 space-y-2">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Photo ID (passport or driver's licence)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Last 2 payslips</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                    <span>3 months of bank statements</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#99DF63] rounded-full mt-2 flex-shrink-0"></div>
                    <span>Details of any existing debts or liabilities</span>
                  </li>
                </ul>
                <p className="mt-3">Your broker will provide a full checklist tailored to your application.</p>
              </FAQItem>
            </div>
          </div>
        </div>
      </section>

      {/* Complaints Link */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <a
              href="/complaints-policy"
              className="flex items-center justify-between bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-[#99DF63]/20 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#00594C]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#00594C]">Complaints & Dispute Resolution Policy</h3>
                  <p className="text-gray-500 text-sm">Learn about our formal complaints process</p>
                </div>
              </div>
              <ChevronDown className="w-5 h-5 text-[#00594C] transform -rotate-90 group-hover:translate-x-1 transition-transform duration-200" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Support;
